import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { dispatchWithAlert } from 'dashboard/helper/dispatchWithAlert';

/**
 * Composable for managing conversation labels
 * @returns {Object} An object containing methods and computed properties for conversation labels
 */
export function useConversationLabels() {
  const store = useStore();
  const getters = useStoreGetters();
  const { t } = useI18n();

  /**
   * The currently selected chat
   * @type {import('vue').ComputedRef<Object>}
   */
  const currentChat = computed(() => getters.getSelectedChat.value);

  /**
   * The ID of the current conversation
   * @type {import('vue').ComputedRef<number|null>}
   */
  const conversationId = computed(() => currentChat.value?.id);

  /**
   * All labels available for the account
   * @type {import('vue').ComputedRef<Array>}
   */
  const accountLabels = computed(() => getters['labels/getLabels'].value);

  /**
   * Labels currently saved to the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const savedLabels = computed(() => {
    return store.getters['conversationLabels/getConversationLabels'](
      conversationId.value
    );
  });

  /**
   * Labels currently active on the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const activeLabels = computed(() =>
    accountLabels.value.filter(({ title }) => savedLabels.value.includes(title))
  );

  /**
   * Labels available but not active on the conversation
   * @type {import('vue').ComputedRef<Array>}
   */
  const inactiveLabels = computed(() =>
    accountLabels.value.filter(
      ({ title }) => !savedLabels.value.includes(title)
    )
  );

  /**
   * Updates the labels for the current conversation
   *
   * A lista enviada é sempre derivada de `savedLabels` (os títulos que a
   * conversa REALMENTE tem), nunca de `activeLabels`. `activeLabels` é a
   * interseção com o cadastro da conta e existe só para pintar o chip com
   * cor/descrição: derivar a escrita dela apagava, em silêncio, qualquer
   * etiqueta ausente do cadastro — etiqueta removida das configurações, ou
   * simplesmente `labels/getLabels` ainda não carregado. O PATCH manda a
   * lista inteira, então o que sai daqui some do banco.
   *
   * @param {string[]} selectedLabels - Array of label titles to be set for the conversation
   * @returns {Promise<void>}
   */
  const onUpdateLabels = async selectedLabels => {
    const uniqueLabels = [...new Set(selectedLabels)];
    await dispatchWithAlert(
      store.dispatch('conversationLabels/update', {
        conversationId: conversationId.value,
        labels: uniqueLabels,
      }),
      { failure: t('CONVERSATION.ASSIGN_LABEL_FAILED') }
    );
  };

  /**
   * Adds a label to the current conversation
   * @param {Object} value - The label object to be added
   * @param {string} value.title - The title of the label to be added
   */
  const addLabelToConversation = value => {
    onUpdateLabels([...savedLabels.value, value.title]);
  };

  /**
   * Removes a label from the current conversation
   * @param {string} value - The title of the label to be removed
   */
  const removeLabelFromConversation = value => {
    onUpdateLabels(savedLabels.value.filter(label => label !== value));
  };

  return {
    accountLabels,
    savedLabels,
    activeLabels,
    inactiveLabels,
    addLabelToConversation,
    removeLabelFromConversation,
    onUpdateLabels,
  };
}
